import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Shield, Bug, Rat, Droplets, TreePine, Leaf, Wind, Sparkles, Waves,
  Phone, Star, ArrowRight, CircleDot, ChevronLeft, ChevronRight,
  CheckCircle2, type LucideIcon,
} from "lucide-react";
import SEO from "@/components/SEO";
import WhatsAppIcon from "@/components/ads/WhatsAppIcon";
// @ts-ignore
import workerImage from "@/assets/professional-worker.webp?w=600&format=webp";
import { useTrackedCTA } from "@/hooks/useTrackedCTA";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import heroDesktop from "@/assets/hero-usa-desktop.webp";
import heroMobile from "@/assets/hero-usa-mobile.webp";

// Service images
import imgDedetizacao from "@/assets/services/dedetizacao.webp?w=400&format=webp&quality=70";
import imgDesratizacao from "@/assets/services/desratizacao.webp?w=400&format=webp&quality=70";
import imgCaixaDagua from "@/assets/services/limpeza-caixa-dagua.webp?w=400&format=webp&quality=70";
import imgDescupinizacao from "@/assets/services/descupinizacao.webp?w=400&format=webp&quality=70";
import imgFormigas from "@/assets/services/formigas.webp?w=400&format=webp&quality=70";
import imgFog from "@/assets/services/fog.webp?w=400&format=webp&quality=70";
import imgSanitizacao from "@/assets/services/sanitizacao.webp?w=400&format=webp&quality=70";
import imgTracas from "@/assets/services/anti-tracas.webp?w=400&format=webp&quality=70";
import imgGordura from "@/assets/services/caixa-gordura.webp?w=400&format=webp&quality=70";
import imgPulgas from "@/assets/services/pulgas-carrapatos.webp?w=400&format=webp&quality=70";
import imgAranhas from "@/assets/services/aranhas-escorpioes.webp?w=400&format=webp&quality=70";
import imgPombos from "@/assets/services/afastamento-pombos.webp?w=400&format=webp&quality=70";

const ScorpionIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <ellipse cx="12" cy="17" rx="3" ry="2.5" />
    <path d="M12 14.5V11c0-1.5.8-3 2-4s2.5-2.5 2.5-4" />
    <path d="M16.5 3l.5 2-1.5-.5" />
    <path d="M9 17l-3-3-1.5.5" />
    <path d="M15 17l3-3 1.5.5" />
  </svg>
);

const SpiderIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <ellipse cx="12" cy="14" rx="3" ry="3.5" />
    <circle cx="12" cy="9" r="2" />
    <path d="M10 10L4 6" />
    <path d="M14 10l6-4" />
    <path d="M9.5 15L4 18" />
    <path d="M14.5 15l5.5 3" />
  </svg>
);

const services: { name: string; desc: string; path: string; icon: LucideIcon; img: string }[] = [
  { name: "Dedetização Profissional", desc: "Controle eficaz de todos os tipos de insetos com produtos exclusivos e seguros.", path: "/servicos/dedetizacao", icon: Bug, img: imgDedetizacao },
  { name: "Desratização", desc: "Eliminação segura de roedores e afastamento de pombos.", path: "/servicos/desratizacao", icon: Rat, img: imgDesratizacao },
  { name: "Limpeza de Caixa D'Água", desc: "Higienização completa para garantir água limpa e saudável.", path: "/servicos/limpeza-caixa-dagua", icon: Droplets, img: imgCaixaDagua },
  { name: "Descupinização", desc: "Tratamento especializado para eliminação total de cupins.", path: "/servicos/descupinizacao", icon: TreePine, img: imgDescupinizacao },
  { name: "Tratamento contra Formigas", desc: "Soluções eficientes para eliminar colônias de formigas.", path: "/servicos/formigas", icon: Leaf, img: imgFormigas },
  { name: "FOG – Fumigação", desc: "Fumigação profissional para grandes áreas e ambientes.", path: "/servicos/fog", icon: Wind, img: imgFog },
  { name: "Sanitização de Ambientes", desc: "Desinfecção e higienização profunda de espaços.", path: "/servicos/sanitizacao", icon: Sparkles, img: imgSanitizacao },
  { name: "Tratamento Anti-Traças", desc: "Proteção completa contra traças em roupas e documentos.", path: "/servicos/anti-tracas", icon: Shield, img: imgTracas },
  { name: "Limpeza de Caixa de Gordura", desc: "Limpeza e manutenção preventiva de caixas de gordura.", path: "/servicos/caixa-gordura", icon: Waves, img: imgGordura },
  { name: "Pulgas e Carrapatos", desc: "Eliminação completa de pulgas e carrapatos em ambientes e pets.", path: "/servicos/pulgas-carrapatos", icon: Bug, img: imgPulgas },
  { name: "Aranhas e Escorpiões", desc: "Combate profissional a aranhas e escorpiões perigosos.", path: "/servicos/aranhas-escorpioes", icon: CircleDot, img: imgAranhas },
  { name: "Afastamento de Pombos", desc: "Métodos humanitários para afastar pombos do seu imóvel.", path: "/servicos/afastamento-pombos", icon: Wind, img: imgPombos },
];

const featuredTestimonials = [
  {
    quote: "Gostaria de parabenizar o comprometimento de todos da equipe USA - desde o atendimento até a ponta com os técnicos - sempre dispostos a atender nossas demandas, por mais repentinas que sejam, com respeito e sobretudo qualidade de quem realmente conhece e se importa com seus clientes.",
    company: "Rede Acreditar Oncologia",
    initials: "RA",
  },
  {
    quote: "Ficamos muito satisfeitos com o serviço da dedetizadora Ultra Serviços Ambientais!! Somos um Grupo grande no seguimento hospitalar e sempre contamos com um atendimento preciso e de qualidade.",
    company: "Hospital Santa Lúcia",
    initials: "SL",
  },
  {
    quote: "Hoje venho prestigiar o trabalho que vem sendo dissolvido em nossas empresas, trabalho prestado pela dedetizadora ULTRA SERVIÇOS AMBIENTAIS.",
    company: "Rede Subway",
    contact: "Alexandre",
    initials: "RS",
  },
];

const googleReviews = [
  { name: "Weverson", date: "2023-11-29", text: "Fui muito bem atendido, é não precisei sair de casa.", rating: 5, initial: "W" },
  { name: "Eliete Alves", date: "2023-10-25", text: "Há mais de 10 anos confio a dedetização da minha casa a vcs! Gratidão.", rating: 5, initial: "E" },
  { name: "Ana Rocha", date: "2023-10-10", text: "Um excelente trabalho, o Paulo é extremamente profissional e rápido. Super indico!", rating: 5, initial: "A" },
];

const stats = [
  { value: "20+", label: "Anos de mercado" },
  { value: "50.000+", label: "Clientes atendidos" },
  { value: "✔", label: "Resultado satisfatório ou seu dinheiro de volta" },
  { value: "24h", label: "Atendimento rápido" },
];

const faqs = [
  { q: "A dedetização faz mal para pessoas ou pets?", a: "Não. Utilizamos produtos profissionais e técnicas seguras, que não oferecem risco para pessoas, crianças ou pets quando aplicados corretamente. Nosso foco é segurança total durante e após o serviço." },
  { q: "Preciso sair do local durante a aplicação?", a: "Na maioria dos casos, não. Nossos métodos permitem a aplicação sem cheiro e sem necessidade de evacuação do imóvel, com total segurança para todos os presentes." },
  { q: "Quanto tempo dura o efeito da dedetização?", a: "O efeito varia conforme o tipo de praga e o ambiente, mas geralmente dura de 3 a 6 meses. Oferecemos garantia em todos os serviços realizados." },
  { q: "Em quanto tempo as pragas começam a desaparecer?", a: "Os resultados começam a ser visíveis nas primeiras 24 a 48 horas após a aplicação. Em alguns casos, o efeito residual continua agindo por semanas." },
  { q: "Vocês atendem residências e empresas?", a: "Sim! Atendemos residências, empresas, condomínios, hospitais, restaurantes e todos os tipos de estabelecimentos comerciais em Brasília e região." },
  { q: "Qual a melhor dedetizadora de Brasília?", a: "A Dedetizadora USA (Ultra Serviços Ambientais) é referência em Brasília com mais de 20 anos de mercado, mais de 50.000 clientes atendidos e garantia de resultado satisfatório ou seu dinheiro de volta. Atendemos todas as 35 regiões do DF: Plano Piloto, Asa Sul, Asa Norte, Lago Sul, Lago Norte, Sudoeste, Taguatinga, Águas Claras, Guará, Ceilândia, Samambaia, Vicente Pires, Sobradinho, Planaltina, Gama, Santa Maria, Recanto das Emas, São Sebastião, Paranoá, Itapoã e muito mais." },
  { q: "Quanto custa uma dedetização em Brasília?", a: "O valor da dedetização varia conforme o tamanho do imóvel e tipo de praga. Oferecemos orçamento gratuito e sem compromisso. Entre em contato pelo WhatsApp: (61) 99293-8591." },
];

const Index = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const { whatsAppUrl, phone, handleWhatsAppClick, handlePhoneClick } = useTrackedCTA();
  const location = useLocation();

  // Scroll to section based on route (mantém /servicos e /depoimentos)
  useEffect(() => {
    const path = location.pathname.replace(/\/$/, "");
    let targetId = "";
    if (path === "/servicos") targetId = "servicos";
    else if (path === "/depoimentos") targetId = "depoimentos";
    if (targetId) {
      setTimeout(() => {
        document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  }, [location.pathname]);

  const nextTestimonial = () => setCurrentTestimonial((p) => (p + 1) % featuredTestimonials.length);
  const prevTestimonial = () => setCurrentTestimonial((p) => (p - 1 + featuredTestimonials.length) % featuredTestimonials.length);

  // Theme tokens (verde escuro vibrante)
  const BG = "#0a1f14";
  const BG_2 = "#0d2a1c";
  const GREEN = "#22c46a";
  const GREEN_DARK = "#1d8a4f";
  const ORANGE = "#ff7a1a";

  return (
    <>
      <SEO
        title="Dedetização em Brasília com Garantia | Dedetizadora USA"
        description="Dedetização em Brasília e DF com garantia: resultado satisfatório ou seu dinheiro de volta. 20+ anos, produto sem cheiro e seguro para pets. Orçamento grátis: (61) 99293-8591."

        keywords="dedetização, detetização, dedetizadora, detetizadora, dedetização profissional, detetização profissional, dedetização residencial, dedetização comercial, detetização residencial, detetização comercial, dedetização com garantia, dedetização segura, dedetização para barata, detetização para barata, dedetização sem cheiro, dedetização segura para pets, dedetização perto de mim, detetização perto de mim, dedetizadora perto de mim, detetizadora perto de mim, empresa de dedetização, empresa de detetização, controle de pragas, matar baratas, eliminar baratas, dedetização brasília, detetização brasília, dedetizadora brasília, detetizadora brasília, dedetização DF, detetização DF, dedetizadora DF, detetizadora DF, dedetizadora em brasília, detetizadora em brasília, dedetização residencial DF, dedetização comercial DF, desratização, descupinização, sanitização, limpeza caixa d'água, dedetizadora 24 horas, dedetização condomínio, dedetização restaurante, dedetização hospital, melhor dedetizadora, preço dedetização, quanto custa dedetização, dedetizadora asa sul, dedetizadora asa norte, dedetizadora taguatinga, dedetizadora águas claras, dedetizadora lago sul, dedetizadora lago norte, dedetizadora guará, dedetizadora ceilândia, dedetizadora samambaia, dedetizadora vicente pires, dedetizadora sudoeste, dedetizadora noroeste"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://ultraservicos.com.br/#organization",
              "name": "Dedetizadora USA - Ultra Serviços Ambientais",
              "url": "https://ultraservicos.com.br",
              "logo": "https://ultraservicos.com.br/og-image.webp",
              "contactPoint": [
                { "@type": "ContactPoint", "telephone": "+5561992938591", "contactType": "sales", "areaServed": "BR-DF", "availableLanguage": "Portuguese" },
                { "@type": "ContactPoint", "telephone": "+5521964001775", "contactType": "sales", "areaServed": "BR-RJ", "availableLanguage": "Portuguese" }
              ],
              "sameAs": []
            },
            {
              "@type": "WebSite",
              "@id": "https://ultraservicos.com.br/#website",
              "name": "Dedetizadora USA",
              "url": "https://ultraservicos.com.br",
              "publisher": { "@id": "https://ultraservicos.com.br/#organization" }
            },
            {
              "@type": "FAQPage",
              "mainEntity": [
                { "@type": "Question", "name": "A dedetização faz mal para pessoas ou pets?", "acceptedAnswer": { "@type": "Answer", "text": "Não. Utilizamos produtos profissionais e técnicas seguras, que não oferecem risco para pessoas, crianças ou pets quando aplicados corretamente. Nosso foco é segurança total durante e após o serviço." }},
                { "@type": "Question", "name": "Preciso sair do local durante a aplicação?", "acceptedAnswer": { "@type": "Answer", "text": "Na maioria dos casos, não. Nossos métodos permitem a aplicação sem cheiro e sem necessidade de evacuação do imóvel, com total segurança para todos os presentes." }},
                { "@type": "Question", "name": "Quanto tempo dura o efeito da dedetização?", "acceptedAnswer": { "@type": "Answer", "text": "O efeito varia conforme o tipo de praga e o ambiente, mas geralmente dura de 3 a 6 meses. Oferecemos garantia em todos os serviços realizados." }},
                { "@type": "Question", "name": "Em quanto tempo as pragas começam a desaparecer?", "acceptedAnswer": { "@type": "Answer", "text": "Os resultados começam a ser visíveis nas primeiras 24 a 48 horas após a aplicação. Em alguns casos, o efeito residual continua agindo por semanas." }},
                { "@type": "Question", "name": "Vocês atendem residências e empresas?", "acceptedAnswer": { "@type": "Answer", "text": "Sim! Atendemos residências, empresas, condomínios, hospitais, restaurantes e todos os tipos de estabelecimentos comerciais em Brasília e região." }},
                { "@type": "Question", "name": "Qual a melhor dedetizadora de Brasília?", "acceptedAnswer": { "@type": "Answer", "text": "A Dedetizadora USA (Ultra Serviços Ambientais) é referência em Brasília com mais de 20 anos de mercado, mais de 50.000 clientes atendidos e garantia de resultado satisfatório ou seu dinheiro de volta. Atendemos todas as 35 regiões do DF." }},
                { "@type": "Question", "name": "Quanto custa uma dedetização em Brasília?", "acceptedAnswer": { "@type": "Answer", "text": "O valor da dedetização varia conforme o tamanho do imóvel e tipo de praga. Oferecemos orçamento gratuito e sem compromisso. Entre em contato pelo WhatsApp: (61) 99293-8591." }}
              ]
            }
          ]
        }}
      />

      <div className="font-sora text-white" style={{ background: BG }}>
        {/* ============ HERO ============ */}
        <section className="relative overflow-hidden min-h-[640px] md:min-h-[720px] flex items-center" aria-label="Dedetizadora USA - Controle de pragas em Brasília">
          {/* Hero image - desktop */}
          <div className="absolute inset-0 hidden md:block">
            <img src={heroDesktop} alt="Sede da Dedetizadora USA com frota de veículos ao pôr do sol" className="w-full h-full object-cover" width={1920} height={1080} fetchPriority="high" decoding="async" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${BG}f2 0%, ${BG}b3 45%, transparent 100%)` }} />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 60%, ${BG} 100%)` }} />
          </div>
          {/* Hero image - mobile */}
          <div className="absolute inset-0 md:hidden">
            <img src={heroMobile} alt="Sede da Dedetizadora USA com frota de veículos ao pôr do sol" className="w-full h-full object-cover" width={864} height={1535} fetchPriority="high" decoding="async" />
            <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${BG}b3 0%, ${BG}cc 55%, ${BG} 100%)` }} />
          </div>

          <div className="container mx-auto px-4 py-20 md:py-28 relative z-10">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-5 backdrop-blur-sm" style={{ background: `${GREEN}26`, border: `1px solid ${GREEN}4d` }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: GREEN }} />
                <span className="text-xs font-medium" style={{ color: "#a8e6c1" }}>Dedetizadora USA — Brasília/DF</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight mb-6">
                Dedetização profissional
                <br />
                <span style={{ color: GREEN }}>com garantia de resultado</span>
              </h1>

              <p className="text-base md:text-xl text-white/75 leading-relaxed mb-8 max-w-2xl">
                Mais de 20 anos de mercado, mais de 50.000 clientes atendidos e <span className="text-white font-medium">garantia de resultado satisfatório ou seu dinheiro de volta</span>. Produtos seguros para pets e crianças.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                <a
                  href={whatsAppUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleWhatsAppClick("home_hero")}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-semibold transition-all hover:-translate-y-0.5"
                  style={{ background: GREEN, color: BG, boxShadow: `0 12px 40px -10px ${GREEN}99` }}
                >
                  <WhatsAppIcon size={20} />
                  Orçamento Grátis
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <Link
                  to="/servicos"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full font-medium transition-all bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/20"
                >
                  Nossos Serviços <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <Link
                to="/beneficios"
                className="inline-flex items-center gap-2 underline underline-offset-4 transition text-sm font-medium mt-4 text-white/80 hover:text-white"
                style={{ textDecorationColor: `${ORANGE}80` }}
              >
                Indique e ganhe até 100% de desconto →
              </Link>

              {/* Specialty Badges */}
              <div className="mt-8">
                <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-3">
                  🛡️ Especialistas em combate a:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" role="list" aria-label="Especialidades">
                  {[
                    { label: "Baratas de Armário", icon: Bug },
                    { label: "Baratas de Cozinha", icon: Bug },
                    { label: "Escorpiões", icon: ScorpionIcon },
                    { label: "Aranhas Venenosas", icon: SpiderIcon },
                  ].map((badge) => (
                    <span
                      key={badge.label}
                      role="listitem"
                      className="inline-flex items-center justify-center gap-2 text-xs sm:text-sm font-medium py-2.5 px-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/15"
                    >
                      <badge.icon className="w-4 h-4 shrink-0" style={{ color: GREEN }} />
                      {badge.label}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ STATS ============ */}
        <section className="relative -mt-10 z-20 pb-12 px-4" aria-label="Números da Dedetizadora USA">
          <div className="container mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {stats.map((s, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-5 md:p-6 text-center backdrop-blur-md"
                  style={{ background: `${BG_2}e6`, border: `1px solid ${GREEN}33` }}
                >
                  <div className="text-2xl md:text-3xl font-bold" style={{ color: GREEN }}>{s.value}</div>
                  <div className="text-xs md:text-sm text-white/70 mt-1.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ GARANTIA ============ */}
        <section className="py-16 md:py-24 px-4">
          <div className="container mx-auto">
            <div className="relative overflow-hidden rounded-3xl px-6 py-12 md:p-14 flex flex-col md:flex-row gap-6 md:gap-8 items-center text-center md:text-left" style={{ background: `linear-gradient(135deg, #0d4a2a, ${BG_2})`, border: `1px solid ${GREEN}33` }}>
              <div className="absolute top-0 right-0 w-96 h-96 rounded-full blur-[120px]" style={{ background: `${ORANGE}1a` }} />
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center shrink-0 relative z-10" style={{ background: GREEN, boxShadow: `0 10px 30px -8px ${GREEN}80` }}>
                <Shield className="w-10 h-10" style={{ color: BG }} />
              </div>
              <div className="relative z-10">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 leading-tight text-balance">
                  Resultado Satisfatório <span style={{ color: ORANGE }}>ou Seu Dinheiro de Volta</span>
                </h2>
                <p className="text-white/75 leading-relaxed max-w-2xl text-balance">
                  Mais de 20 anos de mercado e mais de 50.000 clientes atendidos. Resultado satisfatório ou seu dinheiro de volta — é a nossa forma de mostrar o compromisso com a sua satisfação.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SERVIÇOS ============ */}
        <section id="servicos" className="py-16 md:py-24 px-4" style={{ background: BG_2 }} aria-label="Serviços de controle de pragas em Brasília">
          <div className="container mx-auto">
            <div className="text-center mb-14">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold mb-3 block" style={{ color: GREEN }}>Nossos Serviços</span>
              <h2 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
                Soluções completas em <br className="hidden md:block" />controle de pragas
              </h2>
              <p className="text-white/65 max-w-xl mx-auto">
                Para sua residência ou empresa — com produtos certificados e equipe treinada.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((s) => (
                <Link
                  key={s.path}
                  to={s.path}
                  className="group block rounded-2xl overflow-hidden transition-all hover:-translate-y-1"
                  style={{ background: BG, border: `1px solid ${GREEN}1f` }}
                >
                  <div className="h-48 overflow-hidden relative">
                    <img src={s.img} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" width={400} height={192} decoding="async" />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 50%, ${BG}cc 100%)` }} />
                    <div className="absolute top-3 left-3 w-10 h-10 rounded-xl flex items-center justify-center backdrop-blur-md" style={{ background: `${GREEN}26`, border: `1px solid ${GREEN}66` }}>
                      <s.icon className="w-5 h-5" style={{ color: GREEN }} />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-semibold text-lg mb-2">{s.name}</h3>
                    <p className="text-sm text-white/60 mb-4 leading-relaxed">{s.desc}</p>
                    <span className="text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all" style={{ color: GREEN }}>
                      Saiba mais <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ============ DEPOIMENTOS ============ */}
        <section id="depoimentos" className="py-16 md:py-24 px-4">
          <div className="container mx-auto">
            <div className="flex flex-col lg:flex-row items-stretch">
              {/* Worker photo */}
              <div className="hidden lg:block w-[360px] shrink-0">
                <img
                  src={workerImage}
                  alt="Profissional da Dedetizadora USA"
                  className="w-full h-full object-cover rounded-l-3xl"
                  loading="lazy"
                  width={360}
                  height={500}
                  decoding="async"
                />
              </div>

              {/* Carousel area */}
              <div className="flex-1 rounded-3xl lg:rounded-l-none p-8 md:p-12 flex flex-col" style={{ background: BG_2, border: `1px solid ${GREEN}1f` }}>
                <div className="text-center mb-6">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold mb-2 block" style={{ color: GREEN }}>A confiança é construída</span>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-balance">Validado por Nossos Clientes</h2>
                  <p className="text-white/60 mt-2 text-balance">A prova de nossa eficácia? As palavras de nossos clientes satisfeitos.</p>
                </div>

                <div className="flex-1 flex flex-col items-center justify-center text-center relative min-h-[200px]">
                  <div key={currentTestimonial} className="flex flex-col items-center animate-fade-in">
                    <p className="text-white/80 italic text-base md:text-lg leading-relaxed max-w-2xl mb-8">
                      "{featuredTestimonials[currentTestimonial].quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-sm" style={{ background: `${GREEN}1f`, border: `1px solid ${GREEN}4d`, color: GREEN }}>
                        {featuredTestimonials[currentTestimonial].initials}
                      </div>
                      <div className="text-left">
                        <span className="font-semibold block">{featuredTestimonials[currentTestimonial].company}</span>
                        {"contact" in featuredTestimonials[currentTestimonial] && (
                          <span className="text-sm" style={{ color: GREEN }}>{(featuredTestimonials[currentTestimonial] as any).contact}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <button onClick={prevTestimonial} className="absolute left-0 md:-left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors" aria-label="Depoimento anterior">
                    <ChevronLeft className="w-6 h-6" style={{ color: GREEN }} />
                  </button>
                  <button onClick={nextTestimonial} className="absolute right-0 md:-right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors" aria-label="Próximo depoimento">
                    <ChevronRight className="w-6 h-6" style={{ color: GREEN }} />
                  </button>
                </div>

                <div className="flex justify-center gap-2 mt-6">
                  {featuredTestimonials.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentTestimonial(i)}
                      className="w-2.5 h-2.5 rounded-full transition-colors"
                      style={{ background: i === currentTestimonial ? GREEN : "rgba(255,255,255,0.2)" }}
                      aria-label={`Depoimento ${i + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Google Reviews */}
            <div className="mt-14">
              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-6">
                <div className="text-center shrink-0">
                  <p className="text-2xl font-bold">Excelente</p>
                  <div className="flex gap-1 justify-center my-2">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className="w-6 h-6" style={{ fill: "#fbbf24", color: "#fbbf24" }} />
                    ))}
                  </div>
                  <p className="text-sm text-white/60">Com base em avaliações reais de clientes</p>
                  <p className="text-xl font-bold mt-2 tracking-wide">
                    <span style={{ color: "#4285F4" }}>G</span>
                    <span style={{ color: "#EA4335" }}>o</span>
                    <span style={{ color: "#FBBC05" }}>o</span>
                    <span style={{ color: "#4285F4" }}>g</span>
                    <span style={{ color: "#34A853" }}>l</span>
                    <span style={{ color: "#EA4335" }}>e</span>
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 flex-1 w-full">
                  {googleReviews.map((r, i) => (
                    <div key={i} className="rounded-xl p-5" style={{ background: BG_2, border: `1px solid ${GREEN}1f` }}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: GREEN, color: BG }}>
                            {r.initial}
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{r.name}</p>
                            <p className="text-xs text-white/80">{r.date}</p>
                          </div>
                        </div>
                        <span className="text-xl font-bold" style={{ color: "#4285F4" }}>G</span>
                      </div>
                      <div className="flex gap-0.5 mb-2">
                        {Array.from({ length: r.rating }).map((_, j) => (
                          <Star key={j} className="w-4 h-4" style={{ fill: "#fbbf24", color: "#fbbf24" }} />
                        ))}
                      </div>
                      <p className="text-sm text-white/70">{r.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FAQ ============ */}
        <section className="py-16 md:py-24 px-4" style={{ background: BG_2 }} aria-label="Perguntas frequentes">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-[0.2em] font-semibold mb-2 block" style={{ color: GREEN }}>Perguntas Frequentes</span>
              <h2 className="text-3xl md:text-4xl font-bold">Tire suas dúvidas</h2>
            </div>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="rounded-xl px-6 border-0" style={{ background: BG, border: `1px solid ${GREEN}1f` }}>
                  <AccordionTrigger className="text-left font-medium text-white hover:no-underline">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/70">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* ============ CTA FINAL ============ */}
        <section className="relative py-20 px-4 overflow-hidden" aria-label="Solicite orçamento">
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${GREEN_DARK}, #0d4a2a)` }} />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[120px]" style={{ background: `${GREEN}33` }} />
          <div className="container mx-auto text-center relative z-10">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-4" style={{ color: GREEN }} />
            <h2 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">Livre-se das pragas hoje mesmo!</h2>
            <p className="text-lg text-white/80 max-w-xl mx-auto mb-8">
              Entre em contato e receba um orçamento gratuito em minutos. Atendemos com garantia de resultado.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={whatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsAppClick("home_cta_bottom")}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-lg transition"
                style={{ background: GREEN, color: BG, boxShadow: `0 12px 40px -10px ${GREEN}99` }}
              >
                <WhatsAppIcon size={22} /> WhatsApp
              </a>
              <a
                href={phone}
                onClick={handlePhoneClick("home_cta_bottom")}
                className="inline-flex items-center justify-center gap-2 border-2 border-white/30 font-bold px-8 py-4 rounded-full text-lg hover:bg-white/10 transition"
              >
                <Phone className="w-5 h-5" /> Ligar Agora
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Index;
